import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { ConfirmModal } from 'components/Shared/ConfirmModal';
import { StepViewer } from 'components/Shared/StepViewer';
import { Form, FormItem, useForm } from 'components/UI/Form';
import { Input } from 'components/UI/Input';
import { Spin } from 'components/UI/Spin';
import { useOpenAccountAuth } from 'hooks/account/useOpenAccountAuth';
import { useHistory } from 'hooks/generic/useHistory';
import { useFireEvent } from 'hooks/useFireEvent';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import * as Styled from './styles';
import { useReturnFormFields } from '../../hooks/useReturnFormFields';
import { InfoBlockLoggedIn } from '../components/InfoBlockLoggedIn';
import { forgotPassword } from 'appRedux/restApi';
import { AccountFormFields } from 'constants/account/account-form-fields';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import { StepViewerEvents } from 'constants/app/eventbus-events';

export const ForgotPassword = () => {
  const forgotPasswordId = useId();
  const { t } = useTranslation();
  const [form] = useForm();
  const { goBack } = useHistory();
  const { isLoggedIn, isLoginPending } = useIsLoggedIn();
  const { openAccountRegister } = useOpenAccountAuth();
  const formFields = useReturnFormFields(form, [AccountFormFields.Email]);
  const { fireEvent } = useFireEvent();
  const { refetch, error, data, isFetching } = useQuery({
    queryKey: [EAccountRouteKeys.ACCOUNT_FORGOT_PASSWORD],
    queryFn: () =>
      forgotPassword({
        email: form.getFieldValue(AccountFormFields.Email)
      }),
    enabled: false,
    retry: false
  });

  const submitForgotPassword = () => {
    form
      .validateFields()
      .then(() => void refetch())
      .catch(() => {});
  };

  useEffect(() => {
    fireEvent({
      key: isFetching
        ? StepViewerEvents.SetNextLoading
        : StepViewerEvents.UnsetNextLoading,
      id: forgotPasswordId
    });
  }, [isFetching]);

  useEffect(() => {
    if (error) {
      message.error(t('account.forgotPasswordError'));
    }
  }, [error]);

  if (isLoggedIn) {
    return <InfoBlockLoggedIn />;
  }

  if (isLoginPending) {
    return (
      <Styled.SpinWrapper>
        <Spin type="absolute" />
      </Styled.SpinWrapper>
    );
  }

  if (data) {
    return (
      <ConfirmModal
        title={t('account.forgotPasswordSuccessTitle')}
        description={t('account.forgotPasswordSuccessDescription')}
        confirmText={t('generic.ok')}
        visible
        onCancel={goBack}
        submitAsCancel
      />
    );
  }
  return (
    <Styled.ModalBody>
      <StepViewer
        header={{
          showStepsProgress: false
        }}
        eventId={forgotPasswordId}
        onFinish={submitForgotPassword}
        steps={[
          {
            title: {
              text: t('account.forgotPasswordStepTitle')
            },
            subtitle: t('account.forgotPasswordStepSubtitle'),
            info: {
              text: (
                <Styled.SignUpInfoBlock>
                  <span>{t('account.loginStepSignupText')}</span>{' '}
                  <Styled.SignUpInfoLink onClick={openAccountRegister}>
                    {t('account.loginStepSignup')}
                  </Styled.SignUpInfoLink>
                </Styled.SignUpInfoBlock>
              )
            },
            nextBtnText: t('account.submit'),
            render: () => (
              <Form layout="vertical" form={form}>
                {formFields.map(field => (
                  <FormItem
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    rules={field.rules}
                  >
                    <Input />
                  </FormItem>
                ))}
              </Form>
            )
          }
        ]}
      />
    </Styled.ModalBody>
  );
};
